import { message } from "ant-design-vue"
import { getAuth } from "firebase/auth"
import { defineStore } from "pinia"
import { JobsService, FirebaseTokenLoader } from "@telarya/telarya-sdk"

import { loadFromLocalStorage, saveToLocalStorage } from "@/components/Other/LocalStorageHelper"
import generateEndpoint from "@/endpoint"
import { createAuthenticatedHttpClient } from "@/services/httpClient"
import { reportError } from "@/utils/reportError"
const storeObject = "Vats"
const storeName = storeObject + "Store"

export const useVatsStore = defineStore(storeName, {
  state: () => ({
    records: [],
    isFetching: false,
    fetchPromise: null,
    isInitialized: false,
    lastFetched: null, // For caching validity
    sdkService: null, // Holds the SDK service instance
    tenantId: null, // Dynamically injected tenant ID
    tenantCountry: null // Dynamically injected tenant ID
  }),
  getters: {
    get(state) {
      if (!this.isInitialized) {
        throw new Error(`${storeName}: Store is not initialized. Call 'initialize()' with a tenant first.`)
      }
      return state.records
    }
  },
  actions: {
    resetState() {
      this.records = []
      this.isFetching = false
      this.fetchPromise = null
      this.isInitialized = false
      this.lastFetched = null
      this.sdkService = null
      this.tenantId = null
      this.tenantCountry = null
    },
    setTenant(tenant) {
      if (!tenant || !tenant.id) {
        throw new Error(`${storeName}: Invalid tenant provided.`)
      }
      this.tenantId = tenant.id
      this.tenantCountry = tenant?.publicAddress?.country
      this.logToConsole(`Tenant set to: ${tenant.id}`, "info")
    },
    initializeSdkService() {
      if (this.sdkService) {
        return
      }
      this.sdkService = new JobsService(
        generateEndpoint("jobs"),
        createAuthenticatedHttpClient(new FirebaseTokenLoader(getAuth()))
      )
    },
    getById(id) {
      if (!this.isInitialized) {
        throw new Error(`${storeName}: Store is not initialized. Call 'initialize()' with a tenant first.`)
      }
      return this.records.find((d) => d.id === id)
    },
    async fetch() {
      if (!this.tenantId) {
        this.logToConsole(`Tenant ID not set. Cannot fetch ${storeObject}.`, "error")
        return
      }

      if (this.isFetching) {
        return this.fetchPromise
      }

      this.isFetching = true
      this.fetchPromise = (async () => {
        try {
          this.logToConsole(`Fetching ${storeObject} for tenant ${this.tenantId} from server...`)
          this.initializeSdkService()
          const activeTenantId = this.tenantId
          const activeTenantCountry = this.tenantCountry
          console.log("Fetching vats for country", activeTenantCountry)
          const records = await this.sdkService.getVatsForCountry(activeTenantCountry)
          if (this.tenantId !== activeTenantId) {
            this.logToConsole(
              `Discarded VAT response for stale tenant ${activeTenantId}; current tenant ${this.tenantId}`,
              "info"
            )
            return
          }
          this.records = records
          this.lastFetched = Date.now()

          saveToLocalStorage(`${storeObject}-${activeTenantId}`, {
            records,
            timestamp: this.lastFetched
          })

          this.logToConsole("Updated cache and localStorage", "info")
        } catch (error) {
          this.notifyUser("Error fetching data. " + error.message, "error")
          reportError(error, {
            message: "Failed to fetch VATs",
            source: "VatsStore.fetch",
            tenantId: this.tenantId,
            tenantCountry: this.tenantCountry
          })
        } finally {
          this.isFetching = false
          this.fetchPromise = null
        }
      })()

      return this.fetchPromise
    },
    initialize(tenant) {
      if (!tenant || !tenant.id) {
        throw new Error(`${storeName}: Invalid tenant provided during initialization.`)
      }
      const tenantChanged = this.tenantId && this.tenantId !== tenant.id
      if (tenantChanged) {
        this.resetState()
      } else if (this.isInitialized) {
        return
      }

      this.setTenant(tenant)
      this.initializeSdkService()

      const cachedData = loadFromLocalStorage(`${storeObject}-${this.tenantId}`)
      if (cachedData) {
        const { records, timestamp } = cachedData
        const isCacheValid = Date.now() - timestamp < 27 * 60 * 60 * 1000 // 27 hours
        if (isCacheValid) {
          this.records = records
          this.lastFetched = timestamp
          this.logToConsole(`Loaded valid ${storeObject} for tenant ${this.tenantId} from localStorage`, "info")
        } else {
          this.logToConsole(`Cached ${storeObject} data for tenant ${this.tenantId} is stale`, "info")
        }
      }

      this.fetch()
        .then(() => this.logToConsole(`Fetched ${storeObject} for tenant ${this.tenantId} and updated cache`, "info"))
        .catch((error) => {
          reportError(error, {
            message: "Failed to fetch VATs during initialization",
            source: "VatsStore.initialize",
            tenantId: this.tenantId,
            tenantCountry: this.tenantCountry
          })
        })

      this.isInitialized = true
    },
    notifyUser(m, type = "info") {
      message.error(storeName + " " + `[${type.toUpperCase()}] ${m}`)
    },
    logToConsole(m, type = "info") {
      console.log(storeName + " " + `[${type.toUpperCase()}] ${m}`)
    }
  }
})
